import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { first } from 'rxjs';
import { CompanyListResponse, UserListResponse } from '../interfaces/api';
import { CompanyInfo, ProductPermissions, Role } from '../interfaces/companies';
import { User } from '../interfaces/users';
import { AuthService } from './auth.service';
import { ConfigService } from './config-service.service';

@Injectable({
  providedIn: 'root'
})
export class RestApiService {
  private httpClient = inject(HttpClient);

  constructor(
    public auth: AuthService,
    private config: ConfigService
  ) { }

  sendFeedback(feedback) {
    const config = this.config.getJson();
    const url = config.backendUrl + "feedback";
    return this.httpClient.post(url, feedback).pipe(first());
  }

  getUsers() {
    const config = this.config.getJson();
    const url = config.backendUrl + "users";
    return this.httpClient.get<UserListResponse>(url).pipe(first());
  }

  getCompanies() {
    const config = this.config.getJson();
    const url = config.backendUrl + "jetbridge/companies";
    return this.httpClient.get<CompanyListResponse>(url).pipe(first());
  }

  getPermissions() {
    const config = this.config.getJson();
    const url = config.backendUrl + "permissions";
    return this.httpClient.get<ProductPermissions[]>(url).pipe(first());
  }

  addCompany(company: CompanyInfo) {
    const config = this.config.getJson();
    const url = config.backendUrl + "company";
    return this.httpClient.post(url, company).pipe(first());
  }

  deleteCompany(company: CompanyInfo) {
    const config = this.config.getJson();
    const url = config.backendUrl + "company/" + company.code;
    return this.httpClient.delete(url).pipe(first());
  }

  addRole(role: Role) {
    const config = this.config.getJson();
    const url = config.backendUrl + "company/roles";
    return this.httpClient.post(url, {
      company: role.company.code,
      role: role.name,
      permissions: role.permissions
    }).pipe(first());
  }

  updateRole(role: Role) {
    const config = this.config.getJson();
    const url = config.backendUrl + "role/permissions";
    return this.httpClient.post(url, {
      company: role.company.code,
      role: role.name,
      permissions: role.permissions
    }).pipe(first());
  }

  deleteRole(role: Role) {
    const config = this.config.getJson();
    const url = config.backendUrl + `company/role/${role.company.code}/${role.name}`;
    return this.httpClient.delete(url).pipe(first());
  }

  addUser(user: User) {
    const config = this.config.getJson();
    const url = config.backendUrl + "user";
    return this.httpClient.post(url, {
      company: user.companyCode,
      email: user.email,
      firstName: user.firstName,
      familyName: user.familyName,
      role: user.role
    }).pipe(first());
  }

  inviteUser(user: User) {
    const config = this.config.getJson();
    const url = config.backendUrl + "invite/" + user.email;
    return this.httpClient.put(url, {}).pipe(first());
  }

  resetPassword(user: User) {
    const config = this.config.getJson();
    const url = config.backendUrl + "user/password/" + user.email;
    return this.httpClient.put(url, {}).pipe(first());
  }

  deleteUser(user: User) {
    const config = this.config.getJson();
    const url = config.backendUrl + "user/" + user.email;
    return this.httpClient.delete(url).pipe(first());
  }

  updateUser(user: User) {
    const config = this.config.getJson();
    const url = config.backendUrl + "user";
    return this.httpClient.put(url, {
      firstName: user.firstName,
      familyName: user.familyName,
      company: user.companyCode,
      email: user.email,
      role: user.role
    }).pipe(first());
  }

  updateProducts(company: CompanyInfo) {
    const config = this.config.getJson();
    const url = config.backendUrl + "company/products";
    return this.httpClient.post(url, company).pipe(first());
  }
}
